import { Outfit } from '../types';
import { targetingService, TargetingContext } from './TargetingService';

/**
 * Try-On specific context for ad targeting
 */
export interface TryOnAdContext {
  /** The outfit being tried on */
  outfit: Outfit;
  /** User's profile picture status */
  hasProfilePicture: boolean;
  /** Try-on session ID */
  sessionId: string;
  /** Try-on status */
  status: 'processing' | 'completed' | 'failed';
  /** Result image URL if completed */
  resultImageUrl?: string;
  /** Error message if failed */
  errorMessage?: string;
  /** User's previous try-on history */
  previousTryOns?: string[];
}

/**
 * Enhanced targeting data for try-on context
 */
export interface TryOnTargetingData {
  /** Fashion-related keywords */
  keywords: string[];
  /** Product categories */
  categories: string[];
  /** Brand information */
  brands: string[];
  /** Style preferences */
  styles: string[];
  /** Event types */
  events: string[];
  /** Price range */
  priceRange?: string;
  /** Try-on specific targeting */
  tryOnContext: {
    /** Whether user is actively trying on */
    isActiveTryOn: boolean;
    /** Try-on completion status */
    completionStatus: 'processing' | 'completed' | 'failed';
    /** User engagement level with try-on feature */
    engagementLevel: 'new' | 'occasional' | 'frequent';
    /** Preferred outfit categories for try-on */
    preferredCategories: string[];
  };
}

/**
 * Service for generating context-aware ad targeting based on try-on activity
 */
export class TryOnAdContextService {
  /**
   * Extract outfit metadata for ad targeting
   */
  extractOutfitMetadata(outfit: Outfit): {
    tags: string[];
    brand?: string;
    category: string;
    priceRange?: string;
    style: string;
  } {
    const metadata: {
      tags: string[];
      brand?: string;
      category: string;
      priceRange?: string;
      style: string;
    } = {
      tags: outfit.tags || [],
      category: 'fashion',
      style: 'casual',
    };

    // Extract brand from AI analysis or tags
    const suggestedBrands = outfit.aiAnalysis?.detectedClothingItems?.flatMap(item => item.suggestedBrands || []) || [];
    if (suggestedBrands.length > 0) {
      metadata.brand = suggestedBrands[0];
    }

    // Determine category from tags or AI analysis
    const categories = ['formal', 'casual', 'business', 'party', 'sports', 'summer', 'winter'];
    const foundCategory = outfit.tags.find(tag => 
      categories.some(cat => tag.toLowerCase().includes(cat.toLowerCase()))
    );
    if (foundCategory) {
      metadata.category = foundCategory.toLowerCase();
    }

    // Determine style from tags
    const styles = ['casual', 'formal', 'trendy', 'classic', 'bohemian', 'minimalist', 'vintage'];
    const foundStyle = outfit.tags.find(tag => 
      styles.some(style => tag.toLowerCase().includes(style.toLowerCase()))
    );
    if (foundStyle) {
      metadata.style = foundStyle.toLowerCase();
    }

    // Estimate price range from tags or description
    if (outfit.tags.some(tag => tag.toLowerCase().includes('luxury') || tag.toLowerCase().includes('designer'))) {
      metadata.priceRange = 'high';
    } else if (outfit.tags.some(tag => tag.toLowerCase().includes('budget') || tag.toLowerCase().includes('affordable'))) {
      metadata.priceRange = 'low';
    } else {
      metadata.priceRange = 'medium';
    }

    return metadata;
  }

  /**
   * Build targeting logic based on outfit metadata
   */
  buildTargetingLogic(outfitMetadata: ReturnType<typeof this.extractOutfitMetadata>): TryOnTargetingData {
    const keywords: string[] = [];
    const categories: string[] = [];
    const brands: string[] = [];
    const styles: string[] = [];
    const events: string[] = [];

    // Add outfit-specific keywords
    keywords.push(...outfitMetadata.tags);
    keywords.push(outfitMetadata.category);
    keywords.push(outfitMetadata.style);
    keywords.push('fashion', 'clothing', 'outfit', 'style');

    // Add categories
    categories.push(outfitMetadata.category);
    categories.push('fashion');
    categories.push('apparel');

    // Add brand if available
    if (outfitMetadata.brand) {
      brands.push(outfitMetadata.brand);
      keywords.push(outfitMetadata.brand);
    }

    // Add style
    styles.push(outfitMetadata.style);

    // Determine relevant events from tags
    const eventKeywords = {
      'wedding': ['wedding', 'formal', 'ceremony'],
      'party': ['party', 'celebration', 'night out'],
      'work': ['business', 'professional', 'office'],
      'casual': ['casual', 'everyday', 'weekend'],
      'date': ['date', 'romantic', 'dinner'],
      'travel': ['travel', 'vacation', 'holiday'],
      'sports': ['sports', 'gym', 'athletic', 'workout'],
    };

    for (const [event, eventTags] of Object.entries(eventKeywords)) {
      if (outfitMetadata.tags.some(tag => 
        eventTags.some(eventTag => tag.toLowerCase().includes(eventTag))
      )) {
        events.push(event);
      }
    }

    return {
      keywords: Array.from(new Set(keywords)), // Remove duplicates
      categories: Array.from(new Set(categories)),
      brands: Array.from(new Set(brands)),
      styles: Array.from(new Set(styles)),
      events: Array.from(new Set(events)),
      priceRange: outfitMetadata.priceRange,
      tryOnContext: {
        isActiveTryOn: true,
        completionStatus: 'processing',
        engagementLevel: 'occasional',
        preferredCategories: [outfitMetadata.category],
      },
    };
  }

  /**
   * Generate context-aware targeting for try-on modal
   */
  generateTryOnTargeting(context: TryOnAdContext): TryOnTargetingData {
    // Extract outfit metadata
    const outfitMetadata = this.extractOutfitMetadata(context.outfit);
    
    // Build base targeting
    const targeting = this.buildTargetingLogic(outfitMetadata);

    // Update try-on specific context
    targeting.tryOnContext.completionStatus = context.status;
    targeting.tryOnContext.isActiveTryOn = context.status === 'processing';

    // Determine engagement level based on previous try-ons
    if (context.previousTryOns) {
      if (context.previousTryOns.length === 0) {
        targeting.tryOnContext.engagementLevel = 'new';
      } else if (context.previousTryOns.length < 5) {
        targeting.tryOnContext.engagementLevel = 'occasional';
      } else {
        targeting.tryOnContext.engagementLevel = 'frequent';
      }
    }

    // Add try-on specific keywords based on status
    if (context.status === 'completed') {
      targeting.keywords.push('virtual try-on', 'fashion technology', 'personalized shopping');
    } else if (context.status === 'processing') {
      targeting.keywords.push('fashion preview', 'style exploration', 'outfit planning');
    }

    // Add engagement-specific targeting
    if (targeting.tryOnContext.engagementLevel === 'new') {
      targeting.keywords.push('new user', 'fashion discovery', 'style guide');
    } else if (targeting.tryOnContext.engagementLevel === 'frequent') {
      targeting.keywords.push('fashion enthusiast', 'style expert', 'trendsetter');
    }

    return targeting;
  }

  /**
   * Integrate with existing ad serving infrastructure
   */
  integrateWithAdServing(tryOnContext: TryOnAdContext, userId?: string): TargetingContext {
    // Generate try-on specific targeting
    const tryOnTargeting = this.generateTryOnTargeting(tryOnContext);

    // Build targeting context for the main targeting service
    const targetingContext: TargetingContext = {
      pageContext: 'outfit-detail',
      pageType: 'outfit-detail',
      outfitData: {
        id: tryOnContext.outfit.id,
        userId: tryOnContext.outfit.userId,
        imageUrl: tryOnContext.outfit.imageUrl,
        tags: tryOnContext.outfit.tags,
        brands: tryOnTargeting.brands,
        categories: tryOnTargeting.categories,
        event: tryOnTargeting.events[0],
        aiAnalysis: {
          clothingItems: tryOnContext.outfit.aiAnalysis?.detectedClothingItems?.map(item => item.type) || [],
          style: tryOnTargeting.styles[0] || 'casual',
          colors: tryOnContext.outfit.aiAnalysis?.detectedClothingItems?.flatMap(item => item.colors) || [],
          occasion: tryOnTargeting.events[0] || 'casual',
        },
      },
      user: undefined, // User context not needed for try-on targeting
    };

    return targetingContext;
  }

  /**
   * Get recommended ad placements for try-on modal
   */
  getRecommendedAdPlacements(context: TryOnAdContext): {
    processing: string[];
    completed: string[];
    failed: string[];
  } {
    const outfitMetadata = this.extractOutfitMetadata(context.outfit);
    
    return {
      processing: [
        'try-on-processing-banner',
        'fashion-discovery-sidebar',
        'style-tips-native',
      ],
      completed: [
        'try-on-result-overlay',
        'similar-outfits-carousel',
        'fashion-brands-banner',
        'shopping-recommendations',
      ],
      failed: [
        'try-on-retry-banner',
        'fashion-alternatives-sidebar',
        'style-guide-native',
      ],
    };
  }

  /**
   * Track try-on ad engagement for optimization
   */
  trackTryOnAdEngagement(
    sessionId: string,
    adSlotId: string,
    eventType: 'impression' | 'click' | 'conversion',
    metadata?: {
      outfitId?: string;
      tryOnStatus?: string;
      engagementLevel?: string;
    }
  ): void {
    try {
      // Send tracking data to analytics
      const trackingData = {
        sessionId,
        adSlotId,
        eventType,
        timestamp: new Date().toISOString(),
        context: 'try-on-modal',
        metadata: {
          ...metadata,
          userAgent: navigator.userAgent,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
        },
      };

      // Use existing analytics infrastructure
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', 'try_on_ad_engagement', {
          event_category: 'ads',
          event_label: adSlotId,
          custom_parameter_1: sessionId,
          custom_parameter_2: eventType,
        });
      }

      // Also send to backend for detailed analysis
      fetch('/api/analytics/try-on-ad-engagement', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trackingData),
      }).catch(error => {
        console.error('Failed to track try-on ad engagement:', error);
      });
    } catch (error) {
      console.error('Error tracking try-on ad engagement:', error);
    }
  }
}

// Export singleton instance
export const tryOnAdContextService = new TryOnAdContextService();
export default tryOnAdContextService;